import { useEffect, useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Package, Check, ChevronsUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useProducts, useAddToWarehouse } from '@/hooks/warehouse';
import { useOrganizations } from '@/hooks/useOrganizations';
import { getThumbnailImageUrl } from '@/lib/cloudinary';
import { cn } from '@/lib/utils';
import FormModal from '@/components/v2/molecules/admin/shared/FormModal';

interface AddToWarehouseModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const formSchema = z.object({
  product_id: z.string().min(1, 'El producto es requerido'),
  organization_id: z.string().min(1, 'La organización es requerida'),
  quantity: z.coerce.number().min(1, 'La cantidad debe ser al menos 1'),
  unit_price: z.coerce.number().min(0, 'El precio no puede ser negativo'),
  min_stock_alert: z.coerce.number().nullable().optional(),
  notes: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

const AddToWarehouseModal = ({ isOpen, onClose }: AddToWarehouseModalProps) => {
  const [productSearchOpen, setProductSearchOpen] = useState(false);
  const [productSearch, setProductSearch] = useState('');

  const { data: products = [] } = useProducts();
  const { data: organizations = [] } = useOrganizations();
  const { mutate: addToWarehouse, isPending } = useAddToWarehouse();

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      product_id: '',
      organization_id: '',
      quantity: 0,
      unit_price: 0,
      min_stock_alert: null,
      notes: '',
    },
  });

  const productId = form.watch('product_id');

  // Filter products based on search
  const filteredProducts = useMemo(() => {
    if (!productSearch.trim()) return products;
    const searchLower = productSearch.toLowerCase();
    return products.filter(
      (p) =>
        p.name.toLowerCase().includes(searchLower) ||
        (p.description && p.description.toLowerCase().includes(searchLower))
    );
  }, [products, productSearch]);

  const selectedProduct = products.find((p) => p.id === productId);

  // Update unit price when product is selected
  useEffect(() => {
    if (productId) {
      const product = products.find((p) => p.id === productId);
      if (product) {
        form.setValue('unit_price', product.price);
      }
    }
  }, [productId, products, form]);

  useEffect(() => {
    if (isOpen) {
      form.reset({
        product_id: '',
        organization_id: '',
        quantity: 0,
        unit_price: 0,
        min_stock_alert: null,
        notes: '',
      });
      setProductSearch('');
    }
  }, [isOpen, form]);

  const handleSubmit = (data: FormData) => {
    addToWarehouse(
      {
        product_id: data.product_id,
        organization_id: data.organization_id,
        quantity: data.quantity,
        unit_price: data.unit_price,
        min_stock_alert: data.min_stock_alert,
        notes: data.notes?.trim() || undefined,
      },
      { onSuccess: onClose }
    );
  };

  const truncateDescription = (desc: string | null, maxLength = 50) => {
    if (!desc) return '';
    return desc.length > maxLength ? `${desc.substring(0, maxLength)}...` : desc;
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title="Agregar al Almacén"
      description="Selecciona un producto del catálogo para agregar al almacén"
      form={form}
      onSubmit={handleSubmit}
      submitLabel="Agregar"
      isLoading={isPending}
      maxWidth="max-w-[500px]"
    >
      <div className="grid gap-4 py-4">
        <FormField
          control={form.control}
          name="product_id"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <FormLabel>Producto</FormLabel>
              <Popover open={productSearchOpen} onOpenChange={setProductSearchOpen}>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={productSearchOpen}
                      className={cn(
                        "w-full justify-between h-auto min-h-10",
                        !field.value && "text-muted-foreground"
                      )}
                    >
                      {selectedProduct ? (
                        <div className="flex items-center gap-3 text-left">
                          {selectedProduct.image_url ? (
                            <img
                              src={getThumbnailImageUrl(selectedProduct.image_url)}
                              alt={selectedProduct.name}
                              className="h-8 w-8 rounded object-cover"
                            />
                          ) : (
                            <div className="h-8 w-8 rounded bg-muted flex items-center justify-center">
                              <Package className="h-4 w-4 text-muted-foreground" />
                            </div>
                          )}
                          <span className="truncate">{selectedProduct.name}</span>
                        </div>
                      ) : (
                        "Buscar producto..."
                      )}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="w-[450px] p-0" align="start">
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder="Buscar producto..."
                      value={productSearch}
                      onValueChange={setProductSearch}
                    />
                    <CommandList>
                      <CommandEmpty>No se encontraron productos</CommandEmpty>
                      <CommandGroup>
                        {filteredProducts.map((product) => (
                          <CommandItem
                            key={product.id}
                            value={product.id}
                            onSelect={() => {
                              form.setValue('product_id', product.id);
                              setProductSearchOpen(false);
                              setProductSearch('');
                            }}
                            className="flex items-center gap-3 py-2 cursor-pointer"
                          >
                            {product.image_url ? (
                              <img
                                src={getThumbnailImageUrl(product.image_url)}
                                alt={product.name}
                                className="h-10 w-10 rounded object-cover flex-shrink-0"
                              />
                            ) : (
                              <div className="h-10 w-10 rounded bg-muted flex items-center justify-center flex-shrink-0">
                                <Package className="h-5 w-5 text-muted-foreground" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">{product.name}</p>
                              <p className="text-sm text-muted-foreground truncate">
                                {truncateDescription(product.description)}
                              </p>
                            </div>
                            <Check
                              className={cn(
                                "h-4 w-4 flex-shrink-0",
                                productId === product.id ? "opacity-100" : "opacity-0"
                              )}
                            />
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid gap-2">
          {products.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No hay productos en el catálogo. Crea uno primero.
            </p>
          )}
        </div>

        <FormField
          control={form.control}
          name="organization_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Organización</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger className="bg-gray-100 hover:bg-gray-200 transition-colors">
                    <SelectValue placeholder="Selecciona una organización" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {organizations.map((org) => (
                    <SelectItem key={org.id} value={org.id}>
                      {org.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="quantity"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Cantidad</FormLabel>
                <FormControl>
                  <Input type="number" min="1" placeholder="0" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="unit_price"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Precio Unitario</FormLabel>
                <FormControl>
                  <Input type="number" step="0.01" min="0" placeholder="0.00" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="min_stock_alert"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Alerta de Stock Mínimo (Opcional)</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  min="0"
                  placeholder="Opcional"
                  {...field}
                  value={field.value ?? ''}
                  onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                />
              </FormControl>
              <p className="text-xs text-muted-foreground">
                Dejar vacío para no recibir alertas de stock
              </p>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Notas (opcional)</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Notas adicionales..."
                  rows={2}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </FormModal>
  );
};

export default AddToWarehouseModal;
